import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { DrawItem } from '@app/modules/model/draw';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { syncService } from '@app/modules/sync';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

/************************************/
/* AREA COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-draw-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiDrawEdit extends languageSupport implements OnInit, OnChanges, OnDestroy {    
    @Input('draw') _draw: DrawItem = null; 

    get DrawShapes(){
        if (this._draw){
            switch(this._draw.type){
                case 'BAR':
                    return [ 'LINEAR', 'TOLEFT', 'TORIGHT', 'USHAPE' ];
                case 'TABLE':
                    return [ 'SQUARE', 'CIRCLE' ];
                case 'DOOR':
                    return [ 'TOLEFT', 'TORIGHT' ];
                case 'STAIRS':
                    return [ 'SIMPLE', 'DOUBLE', 'SNAIL' ];
                case 'GENERIC':
                    return [ 'GENRCT', 'GENRND' ];
                }
        }

        return [];
    }

    get CanIndex(){
        return (this._draw.type == 'VERTEX');
    }

    get CanCoordX(){
        return true;
    }

    get CanCoordY(){
        return true;
    }

    get CanCoordW(){
        switch(this._draw.type){
            case 'VERTEX':
                return false;
        }

        return true;
    }

    get CanCoordH(){
        switch(this._draw.type){
            case 'VERTEX':
                return false;
            case 'DOOR':
                return false;
            case 'WINDOW':
                return false;
        }

        return true;
    }

    get CanRotate(){
        return (this._draw.type != 'VERTEX');
    }

    private _drawForm = null;   
    get DrawForm(){
        if (this._drawForm == null){
            this.LoadDrawForm();
        }
        return this._drawForm;
    }

    private _drawCoord(){
        if (this._draw){
            return this._draw.coordinates;
        }

        return { x: 10, y: 10, w: 90, h: 90, r: 0 };
    }    

    private get TableChairs(){
        if (this._draw.type == 'TABLE'){
            if (this._draw.custom && ('chairs' in this._draw.custom)){
                return this._draw.custom['chairs'];
            }
            return 0;
        }
        return null;
    }

    private LoadDrawForm(){
        let _coord = this._drawCoord();

        let _coord_x = _coord.x;
        let _coord_y = _coord.y;
        let _coord_w = _coord.w;
        let _coord_h = _coord.h;
        let _coord_r = _coord.r;

        let _chairs = this.TableChairs;
        let _v_order = this._draw.sort || 0;

        if (!this._drawForm){
            this._drawForm = new FormGroup({
                v_order: new FormControl(_v_order, [
                    Validators.required
                ]),
                chairs: new FormControl(_chairs, [
                    // no validators required
                ]),  
                coord_x: new FormControl(_coord_x, [
                    Validators.required
                ]),
                coord_y: new FormControl(_coord_y, [
                    Validators.required
                ]),
                coord_w: new FormControl(_coord_w, [
                    Validators.required
                ]),
                coord_h: new FormControl(_coord_h, [
                    Validators.required
                ]),
                coord_r: new FormControl(_coord_r, [
                    Validators.required
                ])
            });
        }
        else {
            this._drawForm.patchValue({
                v_order: _v_order,
                chairs: _chairs, 
                coord_x: _coord_x, 
                coord_y: _coord_y, 
                coord_w: _coord_w, 
                coord_h: _coord_h,
                coord_r: _coord_r,
            }); 
        }
    
        this._drawForm.markAsPristine();
        setTimeout(() => {
            this._drawForm.markAsPristine();
        }, 0);   
        
        this.change.markForCheck();
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private sync: syncService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _draw_subscription = null;

    ngOnInit() {
        // nothig to do
    }

    ngOnChanges() {
        if (this._draw_subscription){
            this._draw_subscription.unsubscribe();
            this._draw_subscription = null;
        }

        if (this._draw){
            this._draw_subscription = this._draw.OnRefresh.subscribe(
            data => {
                this.LoadDrawForm();    
            });
        }

        this.LoadDrawForm();        
    }

    ngOnDestroy() {
        super.OnDestroy();

        if (this._draw_subscription){
            this._draw_subscription.unsubscribe();
            this._draw_subscription = null;
        }
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    @Output('onChange') _onChange = new EventEmitter<any>();     
    OnChange(){
        this._onChange.emit();
    }

    @Output('onDelete') _onDelete = new EventEmitter<any>();     
    OnDelete(){
        this._onDelete.emit();
    }

    HasShape(shape){
        return (this._draw.shape == shape);
    }

    OnItemIndex(){
        let _sort = parseInt(this.DrawForm.get('v_order').value);
        if (!_sort || (_sort == this._draw.sort)){
            return;
        }

        this._draw.sort = _sort;

        let _vertices = this._draw.area.vertices;
        for (let _vertex of _vertices){
            if (!_vertex.IsValid || (_vertex == this._draw)){
                continue;
            }

            if (_vertex.sort >= this._draw.sort){
                _vertex.sort = _vertex.sort + 1;
            }
        }
        
        this.OnChange();
    }

    OnItemChairs(){
        let _obj1 = this._draw.custom || {};
        let _obj2 = {
            chairs: this.DrawForm.get('chairs').value
        }

        this._draw.custom = Object.assign({}, _obj1, _obj2);   
        this.OnChange();
    }

    OnItemCoords(){
        this._draw.coordinates = {
            x: this.DrawForm.get('coord_x').value,
            y: this.DrawForm.get('coord_y').value,
            w: this.DrawForm.get('coord_w').value,
            h: this.DrawForm.get('coord_h').value,
            r: this.DrawForm.get('coord_r').value,
        };

        this.OnChange();
    }

    OnItemShape(shape){
        this._draw.shape = shape;
        this.OnChange();
    }

    async OnDeleteItem(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_drawitem_delete_confirm'),
            message: this.lang.tr('@place_drawitem_delete_message', [ this.lang.tr('@place_view_area_' + this._draw.type + '_item').toLowerCase() ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._onDelete.next(this._draw);
            }
        });

        await alert.present();        
    }
}
